import { useEffect, useRef, useState } from "react";
import type {
  PrescriptionItem,
  RestorationPlan,
  ToothPlacementAdjustment,
  ViewMode,
  ViewPreset,
} from "../../types/dental";

type CrownVisibilityMode = "active_only" | "all_visible";
type InteractionMode = "normal" | "pick_restoration_for_edit";
type GeometryDisplayMode = "blocks" | "realistic";

interface ViewportToolbarProps {
  prescriptionItem: PrescriptionItem | null;
  plan: RestorationPlan | null;
  viewMode: ViewMode;
  isDirty: boolean;
  isSaving: boolean;
  isDebugPanelOpen: boolean;
  crownVisibilityMode: CrownVisibilityMode;
  interactionMode: InteractionMode;
  geometryDisplayMode: GeometryDisplayMode;
  hasAnyRestoration: boolean;
  jawOpenAngleDeg: number;

  selectedToothId: string | null;
  selectedToothAdjustment: ToothPlacementAdjustment;

  onSetSelectedToothRotationDeg: (
    axisIndex: 0 | 1 | 2,
    value: number
  ) => void;
  onSetSelectedToothTranslation: (
    axisIndex: 0 | 1 | 2,
    value: number
  ) => void;
  onResetSelectedToothAdjustment: () => void;

  onCreatePlan: () => void;
  onSave: () => void;
  onSetViewMode: (mode: ViewMode) => void;
  onSetCrownVisibilityMode: (mode: CrownVisibilityMode) => void;
  onSetGeometryDisplayMode: (mode: GeometryDisplayMode) => void;
  onFocusTarget: () => void;
  onToggleDebugPanel: () => void;
  onBeginPickRestorationForEdit: () => void;
  onCancelPickRestorationForEdit: () => void;
  onSetJawOpenAngleDeg: (value: number) => void;

  onFitToView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onSetViewPreset: (preset: ViewPreset) => void;
}

function ToolButton({
  icon,
  label,
  disabled = false,
  active = false,
  onClick,
}: {
  icon: string;
  label: string;
  disabled?: boolean;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={`toolbar-button ${active ? "toolbar-button--active" : ""}`}
      title={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: 34,
        height: 34,
        padding: "0 8px",
        border: "1px solid #d7dce3",
        borderRadius: 8,
        background: active ? "#e8f1ff" : "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span className="toolbar-button__icon">{icon}</span>
    </button>
  );
}

function Divider({ tall = false }: { tall?: boolean }) {
  return (
    <div
      className="toolbar-divider"
      style={{
        width: 1,
        height: tall ? 30 : 24,
        background: "#d7dce3",
        margin: "0 2px",
        alignSelf: "center",
      }}
    />
  );
}

function AxisSlider({
  id,
  axisLabel,
  value,
  min,
  max,
  step,
  showDegree = false,
  onChange,
}: {
  id: string;
  axisLabel: string;
  value: number;
  min: number;
  max: number;
  step: number;
  showDegree?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div
      className="toolbar-axis-slider"
      style={{
        display: "grid",
        gridTemplateColumns: "18px 110px 48px",
        alignItems: "center",
        gap: 6,
      }}
    >
      <label htmlFor={id} className="toolbar-hint" style={{ fontWeight: 600 }}>
        {axisLabel}
      </label>

      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: 110 }}
      />

      <span className="toolbar-hint" style={{ minWidth: 44 }}>
        {step >= 1
          ? `${Math.round(value)}${showDegree ? "°" : ""}`
          : `${value.toFixed(1)}`}
      </span>
    </div>
  );
}

function ToothAdjustmentPanel({
  selectedToothId,
  selectedToothAdjustment,
  onSetSelectedToothRotationDeg,
  onSetSelectedToothTranslation,
  onResetSelectedToothAdjustment,
}: {
  selectedToothId: string;
  selectedToothAdjustment: ToothPlacementAdjustment;
  onSetSelectedToothRotationDeg: (
    axisIndex: 0 | 1 | 2,
    value: number
  ) => void;
  onSetSelectedToothTranslation: (
    axisIndex: 0 | 1 | 2,
    value: number
  ) => void;
  onResetSelectedToothAdjustment: () => void;
}) {
  const [rx, ry, rz] = selectedToothAdjustment.rotationDeg;
  const [tx, ty, tz] = selectedToothAdjustment.translation;

  return (
    <div
      className="toolbar-tooth-adjustment-panel"
      style={{
        display: "grid",
        gridTemplateColumns: "92px 1fr 1fr",
        gap: 12,
        alignItems: "start",
        padding: "8px 10px",
        border: "1px solid #d7dce3",
        borderRadius: 10,
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          alignItems: "flex-start",
        }}
      >
        <div className="toolbar-hint" style={{ fontWeight: 700 }}>
          Tooth {selectedToothId}
        </div>

        <button
          type="button"
          className="toolbar-button"
          title="Reset tooth rotation and movement"
          onClick={onResetSelectedToothAdjustment}
          style={{
            padding: "0 10px",
            minWidth: 40,
            height: 30,
            border: "1px solid #d7dce3",
            borderRadius: 8,
            background: "#fff",
            cursor: "pointer",
          }}
        >
          ↺
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: "repeat(4, auto)",
          gap: 6,
        }}
      >
        <div className="toolbar-hint" style={{ fontWeight: 700 }}>
          Rotation
        </div>

        <AxisSlider
          id="tooth-rotation-x"
          axisLabel="X"
          value={rx}
          min={-180}
          max={180}
          step={1}
          showDegree
          onChange={(value) => onSetSelectedToothRotationDeg(0, value)}
        />
        <AxisSlider
          id="tooth-rotation-y"
          axisLabel="Y"
          value={ry}
          min={-180}
          max={180}
          step={1}
          showDegree
          onChange={(value) => onSetSelectedToothRotationDeg(1, value)}
        />
        <AxisSlider
          id="tooth-rotation-z"
          axisLabel="Z"
          value={rz}
          min={-180}
          max={180}
          step={1}
          showDegree
          onChange={(value) => onSetSelectedToothRotationDeg(2, value)}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: "repeat(4, auto)",
          gap: 6,
        }}
      >
        <div className="toolbar-hint" style={{ fontWeight: 700 }}>
          Move
        </div>

        <AxisSlider
          id="tooth-move-x"
          axisLabel="X"
          value={tx}
          min={-5}
          max={5}
          step={0.1}
          onChange={(value) => onSetSelectedToothTranslation(0, value)}
        />
        <AxisSlider
          id="tooth-move-y"
          axisLabel="Y"
          value={ty}
          min={-5}
          max={5}
          step={0.1}
          onChange={(value) => onSetSelectedToothTranslation(1, value)}
        />
        <AxisSlider
          id="tooth-move-z"
          axisLabel="Z"
          value={tz}
          min={-5}
          max={5}
          step={0.1}
          onChange={(value) => onSetSelectedToothTranslation(2, value)}
        />
      </div>
    </div>
  );
}

export function ViewportToolbar({
  prescriptionItem,
  plan,
  viewMode,
  isDirty,
  isSaving,
  isDebugPanelOpen,
  crownVisibilityMode,
  interactionMode,
  geometryDisplayMode,
  hasAnyRestoration,
  jawOpenAngleDeg,
  selectedToothId,
  selectedToothAdjustment,
  onSetSelectedToothRotationDeg,
  onSetSelectedToothTranslation,
  onResetSelectedToothAdjustment,
  onCreatePlan,
  onSave,
  onSetViewMode,
  onSetCrownVisibilityMode,
  onSetGeometryDisplayMode,
  onFocusTarget,
  onToggleDebugPanel,
  onBeginPickRestorationForEdit,
  onCancelPickRestorationForEdit,
  onSetJawOpenAngleDeg,
  onFitToView,
  onZoomIn,
  onZoomOut,
  onSetViewPreset,
}: ViewportToolbarProps) {
  const hasPrescription = Boolean(prescriptionItem);
  const hasPlan = Boolean(plan);
  const isPickingRestoration = interactionMode === "pick_restoration_for_edit";

  const [showViewMenu, setShowViewMenu] = useState(false);
  const viewMenuRef = useRef<HTMLDivElement | null>(null);

  void onZoomIn;
  void onZoomOut;

  useEffect(() => {
    const handleDocumentPointerDown = (event: MouseEvent) => {
      if (!viewMenuRef.current) return;
      if (viewMenuRef.current.contains(event.target as Node)) return;
      setShowViewMenu(false);
    };

    if (showViewMenu) {
      document.addEventListener("mousedown", handleDocumentPointerDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleDocumentPointerDown);
    };
  }, [showViewMenu]);

  const viewPresetOptions: Array<{ value: ViewPreset; label: string }> = [
    { value: "front", label: "Front" },
    { value: "back", label: "Back" },
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
    { value: "top", label: "Top" },
    { value: "bottom", label: "Bottom" },
  ];

  return (
    <div
      className="viewport-toolbar"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: 12,
        width: "100%",
      }}
    >
      <div
        className="viewport-toolbar__left"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
          minWidth: 0,
        }}
      >
        <div
          className="viewport-toolbar__row"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 6,
          }}
        >
          <div
            className="toolbar-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
            }}
          >
            <ToolButton
              icon="➕"
              label="Create restoration plan"
              disabled={!hasPrescription || hasPlan}
              onClick={onCreatePlan}
            />
            <ToolButton
              icon="💾"
              label={isSaving ? "Saving..." : "Save case"}
              disabled={!isDirty || isSaving}
              onClick={onSave}
            />
            <ToolButton
              icon="🎯"
              label="Focus selected target"
              disabled={!hasPrescription}
              onClick={onFocusTarget}
            />
          </div>

          <Divider />

          <div
            className="toolbar-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
            }}
          >
            <ToolButton
              icon="⬆️"
              label="Show upper jaw"
              active={viewMode === "upper"}
              onClick={() => onSetViewMode("upper")}
            />
            <ToolButton
              icon="⬇️"
              label="Show lower jaw"
              active={viewMode === "lower"}
              onClick={() => onSetViewMode("lower")}
            />
            <ToolButton
              icon="👁"
              label="Show both jaws"
              active={viewMode === "both"}
              onClick={() => onSetViewMode("both")}
            />
          </div>

          <Divider />

          <div
            className="toolbar-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
            }}
          >
            <label
              htmlFor="geometry-display-mode"
              className="toolbar-hint"
              style={{ fontWeight: 600 }}
            >
              Geo
            </label>
            <select
              id="geometry-display-mode"
              className="top-select"
              value={geometryDisplayMode}
              onChange={(e) =>
                onSetGeometryDisplayMode(
                  e.target.value as GeometryDisplayMode
                )
              }
              title="Tooth representation mode"
              style={{
                width: 86,
                height: 34,
                borderRadius: 8,
                border: "1px solid #d7dce3",
                background: "#fff",
                padding: "0 8px",
              }}
            >
              <option value="blocks">Blocks</option>
              <option value="realistic">Realistic</option>
            </select>
          </div>

          <Divider />

          <div
            className="toolbar-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
            }}
          >
            <ToolButton icon="⛶" label="Fit to view" onClick={onFitToView} />

            <div ref={viewMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                title="View presets"
                aria-label="View presets"
                onClick={() => setShowViewMenu((v) => !v)}
                style={{
                  width: 34,
                  height: 34,
                  border: "1px solid #d7dce3",
                  borderRadius: 8,
                  background: showViewMenu ? "#e8f1ff" : "#fff",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                🧊
              </button>

              {showViewMenu && (
                <div
                  style={{
                    position: "absolute",
                    top: 38,
                    left: 0,
                    minWidth: 110,
                    background: "#fff",
                    border: "1px solid #d7dce3",
                    borderRadius: 8,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                    padding: 4,
                    zIndex: 20,
                  }}
                >
                  {viewPresetOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        onSetViewPreset(option.value);
                        setShowViewMenu(false);
                      }}
                      style={{
                        width: "100%",
                        height: 32,
                        border: "none",
                        background: "transparent",
                        borderRadius: 6,
                        textAlign: "left",
                        padding: "0 10px",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f4f7fb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Divider />

          <div
            className="toolbar-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
            }}
          >
            <ToolButton
              icon="🧪"
              label="Debug controls"
              active={isDebugPanelOpen}
              onClick={onToggleDebugPanel}
            />
          </div>
        </div>

        <div
          className="viewport-toolbar__row"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 10,
          }}
        >
          <button
            type="button"
            title={
              isPickingRestoration
                ? "Click a restoration in the viewport"
                : "Edit selected treatment"
            }
            disabled={!hasAnyRestoration}
            onClick={
              isPickingRestoration
                ? onCancelPickRestorationForEdit
                : onBeginPickRestorationForEdit
            }
            className={`toolbar-button ${
              isPickingRestoration ? "toolbar-button--active" : ""
            }`}
            style={{
              height: 34,
              width: 80,
              padding: "0 12px",
              border: "1px solid #d7dce3",
              borderRadius: 8,
              background: isPickingRestoration ? "#e8f1ff" : "#fff",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: !hasAnyRestoration ? "not-allowed" : "pointer",
              opacity: !hasAnyRestoration ? 0.55 : 1,
            }}
          >
            <span>✏️</span>
            <span>Edit</span>
          </button>

          {isPickingRestoration && (
            <ToolButton
              icon="❌"
              label="Cancel pick mode"
              onClick={onCancelPickRestorationForEdit}
            />
          )}

          <Divider tall />

          <div
            className="toolbar-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
            }}
          >
            <label
              htmlFor="jaw-open-angle"
              className="toolbar-hint"
              style={{ fontWeight: 600 }}
            >
              Open
            </label>
            <input
              id="jaw-open-angle"
              type="range"
              min={0}
              max={90}
              step={1}
              value={jawOpenAngleDeg}
              onChange={(e) => onSetJawOpenAngleDeg(Number(e.target.value))}
              title={`Jaw opening: ${jawOpenAngleDeg}°`}
              style={{ width: 150 }}
            />
            <span className="toolbar-hint" style={{ minWidth: 36 }}>
              {jawOpenAngleDeg}°
            </span>
          </div>

          <Divider tall />

          <div
            className="toolbar-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "nowrap",
            }}
          >
            <button
              type="button"
              title="Show active restoration only"
              onClick={() => onSetCrownVisibilityMode("active_only")}
              style={{
                height: 34,
                padding: "0 10px",
                border: "1px solid #d7dce3",
                borderRadius: 8,
                background:
                  crownVisibilityMode === "active_only" ? "#e8f1ff" : "#fff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>👑</span>
              <span>Active</span>
            </button>

            <button
              type="button"
              title="Show all visible restorations"
              onClick={() => onSetCrownVisibilityMode("all_visible")}
              style={{
                height: 34,
                padding: "0 10px",
                border: "1px solid #d7dce3",
                borderRadius: 8,
                background:
                  crownVisibilityMode === "all_visible" ? "#e8f1ff" : "#fff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>🦷</span>
              <span>All</span>
            </button>
          </div>
        </div>

        {selectedToothId ? (
          <div
            className="viewport-toolbar__row"
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <ToothAdjustmentPanel
              selectedToothId={selectedToothId}
              selectedToothAdjustment={selectedToothAdjustment}
              onSetSelectedToothRotationDeg={onSetSelectedToothRotationDeg}
              onSetSelectedToothTranslation={onSetSelectedToothTranslation}
              onResetSelectedToothAdjustment={onResetSelectedToothAdjustment}
            />
          </div>
        ) : null}

        <div
          className="viewport-toolbar__footer"
          style={{
            minHeight: 18,
            display: "flex",
            alignItems: "center",
          }}
        >
          {!prescriptionItem && (
            <div className="toolbar-hint">Select a prescription to begin.</div>
          )}

          {prescriptionItem && !plan && (
            <div className="toolbar-hint">
              Create a restoration plan to enable editing.
            </div>
          )}

          {selectedToothId == null && prescriptionItem && (
            <div className="toolbar-hint">
              Select a tooth to adjust rotation and movement.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}